import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  useGetVansQuery,
  useGetVanTripQuery,
  useCreateVanTripMutation,
  useLoadTripMutation,
  useStartTripMutation,
  useCreateVanSaleMutation,
  useUnloadTripMutation,
  useCompleteTripMutation,
  useCancelTripMutation,
} from "../../../store/api/vanSalesApi";
import { useGetLocationsQuery } from "../../../store/api/inventoryApi";
import { useGetUsersQuery } from "../../../store/api/userApi";
import { useLazyGetProductsQuery } from "../../../store/api/productsApi";
import { useGetCustomersQuery } from "../../../store/api/salesApi";
import { useGetCurrentCompanyQuery } from "../../../store/api/companyApi";
import { selectUser } from "../../../store/slices/authSlice";
import ProductSearchOption from "../../sales/components/ProductSearchOption";

const STATUS_COLORS = {
  draft: "default",
  loaded: "info",
  in_progress: "warning",
  completed: "success",
  cancelled: "error",
};
const STATUS_LABELS = {
  draft: "Draft",
  loaded: "Loaded",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

function VanTripDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "new";
  const currentUser = useSelector(selectUser);

  const {
    data: trip,
    isLoading: loadingTrip,
    refetch,
  } = useGetVanTripQuery(id, { skip: isNew });
  const { data: vans = [] } = useGetVansQuery();
  const { data: locations = [] } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const { data: customers = [] } = useGetCustomersQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const userList = Array.isArray(usersData)
    ? usersData
    : (usersData?.users ?? []);
  const currency = company?.currency || "USD";
  const defaultVat = Number(company?.vatRate) || 5;

  const [createVanTrip] = useCreateVanTripMutation();
  const [loadTrip] = useLoadTripMutation();
  const [startTrip] = useStartTripMutation();
  const [createVanSale] = useCreateVanSaleMutation();
  const [unloadTrip] = useUnloadTripMutation();
  const [completeTrip] = useCompleteTripMutation();
  const [cancelTrip] = useCancelTripMutation();

  // Create trip form
  const [tripForm, setTripForm] = useState({
    vanId: "",
    salespersonId: currentUser?.id || "",
    warehouseLocationId: "",
    notes: "",
  });

  // Loading items
  const [loadItems, setLoadItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [
    triggerProductSearch,
    { data: productSearchData, isFetching: searchingProducts },
  ] = useLazyGetProductsQuery();

  // Unload items
  const [unloadItems, setUnloadItems] = useState([]);
  const [unloadDialogOpen, setUnloadDialogOpen] = useState(false);

  // Sale dialog
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [saleForm, setSaleForm] = useState({
    customerId: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    salesType: "cash",
    notes: "",
  });
  const [saleItems, setSaleItems] = useState([]);
  const [saleProductSearch, setSaleProductSearch] = useState("");
  const [
    triggerSaleProductSearch,
    { data: saleProductData, isFetching: searchingSaleProducts },
  ] = useLazyGetProductsQuery();

  useEffect(() => {
    if (!isNew && currentUser?.id && !tripForm.salespersonId) {
      setTripForm((prev) => ({ ...prev, salespersonId: currentUser.id }));
    }
  }, [isNew, currentUser]);

  // Initialize unload items from trip items
  useEffect(() => {
    if (trip?.items) {
      setUnloadItems(
        trip.items.map((ti) => ({
          productId: ti.productId,
          productName: ti.product?.name || "",
          loadedQty: Number(ti.loadedQty || 0),
          soldQty: Number(ti.soldQty || 0),
          returnedQty: Number(ti.returnedQty || 0),
          damagedQty: Number(ti.damagedQty || 0),
        })),
      );
    }
  }, [trip]);

  const handleCreateTrip = async () => {
    if (!tripForm.vanId) return alert("Please select a van");
    if (!tripForm.warehouseLocationId)
      return alert("Please select a warehouse");
    try {
      const created = await createVanTrip(tripForm).unwrap();
      navigate(`/van-sales/trips/${created.id}`, { replace: true });
    } catch (err) {
      alert(err?.data?.message || "Error creating trip");
    }
  };

  const handleAddLoadItem = (product) => {
    if (!product) return;
    if (loadItems.find((i) => i.productId === product.id)) return;
    setLoadItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        productSku: product.sku,
        quantity: 1,
      },
    ]);
  };

  const handleLoadTrip = async () => {
    if (loadItems.length === 0) return alert("Add at least one item to load");
    if (loadItems.some((i) => !i.quantity || Number(i.quantity) <= 0)) {
      return alert("All items must have a quantity greater than 0");
    }
    try {
      await loadTrip({
        id: trip.id,
        items: loadItems.map((i) => ({
          productId: i.productId,
          quantity: Number(i.quantity),
        })),
      }).unwrap();
      setLoadItems([]);
      refetch();
    } catch (err) {
      alert(err?.data?.message || "Error loading trip");
    }
  };

  const handleStartTrip = async () => {
    try {
      await startTrip(trip.id).unwrap();
      refetch();
    } catch (err) {
      alert(err?.data?.message || "Error starting trip");
    }
  };

  const handleCompleteTrip = async () => {
    if (
      !window.confirm(
        "Are you sure you want to complete this trip? This will finalize reconciliation.",
      )
    )
      return;
    try {
      await completeTrip(trip.id).unwrap();
      refetch();
    } catch (err) {
      alert(err?.data?.message || "Error completing trip");
    }
  };

  const handleCancelTrip = async () => {
    if (!window.confirm("Are you sure you want to cancel this trip?")) return;
    try {
      await cancelTrip(trip.id).unwrap();
      refetch();
    } catch (err) {
      alert(err?.data?.message || "Error cancelling trip");
    }
  };

  // Sale handling
  const handleAddSaleItem = (product) => {
    if (!product) return;
    setSaleItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unitPrice: product.sellingPrice ?? 0,
        discount: 0,
        vatPercent: defaultVat,
      },
    ]);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const computeSaleLine = (item) => {
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const sub = Math.max(0, qty * price - discount);
    const vatAmount =
      vatPct > 0 ? Math.round(((sub * vatPct) / 100) * 100) / 100 : 0;
    return { vatAmount, lineTotal: Math.round((sub + vatAmount) * 100) / 100 };
  };

  const saleGrandTotal = saleItems.reduce(
    (sum, item) => sum + computeSaleLine(item).lineTotal,
    0,
  );

  const handleCreateSale = async () => {
    if (!saleForm.customerId) return alert("Please select a customer");
    if (!saleForm.invoiceDate) return alert("Invoice date is required");
    if (saleItems.length === 0) return alert("Add at least one item");

    try {
      await createVanSale({
        tripId: trip.id,
        customerId: saleForm.customerId,
        invoiceDate: saleForm.invoiceDate,
        orderDate: saleForm.invoiceDate,
        salesType: saleForm.salesType,
        notes: saleForm.notes,
        payments:
          saleForm.salesType === "cash"
            ? [{ method: "cash", amount: saleGrandTotal }]
            : [],
        items: saleItems.map((item) => ({
          productId: item.productId,
          quantity: toNum(item.quantity),
          unitPrice: toNum(item.unitPrice),
          discount: toNum(item.discount),
          vatPercent: toNum(item.vatPercent),
        })),
      }).unwrap();
      setSaleDialogOpen(false);
      setSaleForm({
        customerId: "",
        invoiceDate: new Date().toISOString().split("T")[0],
        salesType: "cash",
        notes: "",
      });
      setSaleItems([]);
      refetch();
    } catch (err) {
      alert(err?.data?.message || "Error creating van sale");
    }
  };

  // Unload handling
  const handleUnload = async () => {
    const itemsToUnload = unloadItems.filter(
      (i) => Number(i.returnedQty) > 0 || Number(i.damagedQty) > 0,
    );
    if (itemsToUnload.length === 0)
      return alert("Enter returned or damaged quantities");
    try {
      await unloadTrip({
        id: trip.id,
        items: itemsToUnload.map((i) => ({
          productId: i.productId,
          returnedQty: Number(i.returnedQty) || 0,
          damagedQty: Number(i.damagedQty) || 0,
        })),
      }).unwrap();
      setUnloadDialogOpen(false);
      refetch();
    } catch (err) {
      alert(err?.data?.message || "Error unloading trip");
    }
  };

  // ─── Render: New Trip Form ───
  if (isNew) {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
          Create Van Trip
        </Typography>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Van *"
                value={tripForm.vanId}
                onChange={(e) =>
                  setTripForm({ ...tripForm, vanId: e.target.value })
                }
              >
                <MenuItem value="">Select Van</MenuItem>
                {vans
                  .filter((v) => v.isActive)
                  .map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name} ({v.plateNumber})
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Warehouse *"
                value={tripForm.warehouseLocationId}
                onChange={(e) =>
                  setTripForm({
                    ...tripForm,
                    warehouseLocationId: e.target.value,
                  })
                }
              >
                <MenuItem value="">Select Warehouse</MenuItem>
                {locations.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name} ({l.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Salesperson"
                value={tripForm.salespersonId}
                onChange={(e) =>
                  setTripForm({ ...tripForm, salespersonId: e.target.value })
                }
              >
                <MenuItem value="">None</MenuItem>
                {userList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName && u.lastName
                      ? `${u.firstName} ${u.lastName}`
                      : u.email}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Notes"
                value={tripForm.notes}
                onChange={(e) =>
                  setTripForm({ ...tripForm, notes: e.target.value })
                }
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button variant="contained" onClick={handleCreateTrip}>
                  Create Trip
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/van-sales")}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    );
  }

  // ─── Render: Loading / View ───
  if (loadingTrip) return <Typography>Loading trip...</Typography>;
  if (!trip) return <Typography>Trip not found.</Typography>;

  const isDraft = trip.status === "draft";
  const isLoaded = trip.status === "loaded";
  const isInProgress = trip.status === "in_progress";
  const isCompleted = trip.status === "completed";
  const isCancelled = trip.status === "cancelled";
  const canSell = isLoaded || isInProgress;
  const canUnload = isLoaded || isInProgress;
  const canComplete = isLoaded || isInProgress;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Trip: {trip.tripNumber}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.5, alignItems: "center" }}>
            <Chip
              label={STATUS_LABELS[trip.status] || trip.status}
              color={STATUS_COLORS[trip.status] || "default"}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Van: {trip.van?.name} ({trip.van?.plateNumber})
            </Typography>
            {trip.salesperson && (
              <Typography variant="body2" color="text.secondary">
                | Salesperson: {trip.salesperson.firstName}{" "}
                {trip.salesperson.lastName}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {isLoaded && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartTrip}
            >
              Start Trip
            </Button>
          )}
          {canSell && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PointOfSaleIcon />}
              onClick={() => setSaleDialogOpen(true)}
            >
              Create Sale
            </Button>
          )}
          {canUnload && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<AssignmentReturnIcon />}
              onClick={() => setUnloadDialogOpen(true)}
            >
              Unload Van
            </Button>
          )}
          {canComplete && (
            <Button
              variant="contained"
              color="info"
              startIcon={<StopIcon />}
              onClick={handleCompleteTrip}
            >
              Complete Trip
            </Button>
          )}
          {isCompleted && (
            <Button
              variant="outlined"
              startIcon={<ReceiptIcon />}
              onClick={() =>
                navigate(`/van-sales/trips/${trip.id}/reconciliation`)
              }
            >
              Reconciliation
            </Button>
          )}
          {!isCompleted && !isCancelled && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancelTrip}
            >
              Cancel Trip
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate("/van-sales")}>
            Back
          </Button>
        </Box>
      </Box>

      {/* Draft: Load Items */}
      {isDraft && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Load Items into Van
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2, maxWidth: 500 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={productSearchData?.products || []}
              loading={searchingProducts}
              value={null}
              inputValue={productSearch}
              getOptionLabel={(o) =>
                o?.name ? `${o.name} (${o.sku || o.barcode || ""})` : ""
              }
              renderOption={(props, option) => (
                <ProductSearchOption
                  props={props}
                  option={option}
                  currency={company?.currency || 'USD'}
                  decimals={company?.currencyDecimals ?? 2}
                  isSelected={loadItems.some((it) => String(it.productId) === String(option.id))}
                  priceMode="selling"
                />
              )}
              onInputChange={(_, v) => {
                setProductSearch(v);
                if (v && v.length >= 2)
                  triggerProductSearch({ search: v, limit: 20 });
              }}
              onChange={(_, v) => {
                if (v) {
                  handleAddLoadItem(v);
                  setProductSearch("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search product to load"
                  placeholder="Type 2+ chars"
                />
              )}
            />
          </Box>
          {loadItems.length > 0 && (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>
                        Quantity
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 60 }}>
                        Remove
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadItems.map((item, idx) => (
                      <TableRow key={item.productId}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.productSku || "—"}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => {
                              const next = [...loadItems];
                              next[idx].quantity = e.target.value;
                              setLoadItems(next);
                            }}
                            inputProps={{ min: 0.01, step: 0.01 }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setLoadItems(
                                loadItems.filter((_, i) => i !== idx),
                              )
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleLoadTrip}
              >
                Load Van
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Loaded Items */}
      {(trip.items || []).length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Loaded Items
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Loaded</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sold</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Returned</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Damaged</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unit Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trip.items.map((ti) => (
                  <TableRow key={ti.id}>
                    <TableCell>{ti.product?.name || ti.productId}</TableCell>
                    <TableCell>{ti.product?.sku || "—"}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {Number(ti.loadedQty)}
                    </TableCell>
                    <TableCell>{Number(ti.soldQty)}</TableCell>
                    <TableCell>{Number(ti.returnedQty)}</TableCell>
                    <TableCell>{Number(ti.damagedQty)}</TableCell>
                    <TableCell>{Number(ti.unitCost).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Sales made during trip */}
      {(trip.sales || []).length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Sales Made ({trip.sales.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Paid</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trip.sales.map((s) => {
                  const so = s.salesOrder;
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {so?.invoiceNumber || so?.orderNumber}
                      </TableCell>
                      <TableCell>{so?.customer?.name || "—"}</TableCell>
                      <TableCell>
                        {Number(so?.totalAmount || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                      </TableCell>
                      <TableCell>
                        {Number(so?.paidAmount || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                      </TableCell>
                      <TableCell>
                        {Number(so?.balanceAmount || 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Trip Info */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Trip Info
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Warehouse
            </Typography>
            <Typography>{trip.warehouse?.name || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Load Date
            </Typography>
            <Typography>
              {trip.loadDate ? new Date(trip.loadDate).toLocaleString() : "—"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Return Date
            </Typography>
            <Typography>
              {trip.returnDate
                ? new Date(trip.returnDate).toLocaleString()
                : "—"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" color="text.secondary">
              Notes
            </Typography>
            <Typography>{trip.notes || "—"}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ─── Sale Dialog ─── */}
      <Dialog
        open={saleDialogOpen}
        onClose={() => setSaleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Create Van Sale</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Customer *"
                value={saleForm.customerId}
                onChange={(e) =>
                  setSaleForm({ ...saleForm, customerId: e.target.value })
                }
              >
                <MenuItem value="">Select Customer</MenuItem>
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Invoice Date *"
                value={saleForm.invoiceDate}
                onChange={(e) =>
                  setSaleForm({ ...saleForm, invoiceDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Sales Type"
                value={saleForm.salesType}
                onChange={(e) =>
                  setSaleForm({ ...saleForm, salesType: e.target.value })
                }
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: "flex", gap: 1, mb: 2, maxWidth: 400 }}>
            <Autocomplete
              fullWidth
              size="small"
              options={saleProductData?.products || []}
              loading={searchingSaleProducts}
              value={null}
              inputValue={saleProductSearch}
              getOptionLabel={(o) =>
                o?.name ? `${o.name} (${o.sku || ""})` : ""
              }
              onInputChange={(_, v) => {
                setSaleProductSearch(v);
                if (v && v.length >= 2)
                  triggerSaleProductSearch({ search: v, limit: 20 });
              }}
              onChange={(_, v) => {
                if (v) {
                  handleAddSaleItem(v);
                  setSaleProductSearch("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add product"
                  placeholder="Search..."
                />
              )}
            />
          </Box>

          {saleItems.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {saleItems.map((item, idx) => {
                    const { lineTotal } = computeSaleLine(item);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => {
                              const n = [...saleItems];
                              n[idx].quantity = e.target.value;
                              setSaleItems(n);
                            }}
                            sx={{ width: 70 }}
                            inputProps={{ min: 0.01, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.unitPrice}
                            onChange={(e) => {
                              const n = [...saleItems];
                              n[idx].unitPrice = e.target.value;
                              setSaleItems(n);
                            }}
                            sx={{ width: 90 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.discount}
                            onChange={(e) => {
                              const n = [...saleItems];
                              n[idx].discount = e.target.value;
                              setSaleItems(n);
                            }}
                            sx={{ width: 70 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.vatPercent}
                            onChange={(e) => {
                              const n = [...saleItems];
                              n[idx].vatPercent = e.target.value;
                              setSaleItems(n);
                            }}
                            sx={{ width: 60 }}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {lineTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setSaleItems(
                                saleItems.filter((_, i) => i !== idx),
                              )
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {saleItems.length > 0 && (
            <Typography sx={{ mt: 2, fontWeight: 700, textAlign: "right" }}>
              Grand Total: {currency} {saleGrandTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSale}>
            Create Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Unload Dialog ─── */}
      <Dialog
        open={unloadDialogOpen}
        onClose={() => setUnloadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Unload Van - Return Items
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the quantities returned to warehouse and any damaged items.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Loaded</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Returned Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Damaged Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unloadItems.map((item, idx) => (
                  <TableRow key={item.productId}>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>{item.loadedQty}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.returnedQty}
                        onChange={(e) => {
                          const n = [...unloadItems];
                          n[idx].returnedQty = e.target.value;
                          setUnloadItems(n);
                        }}
                        sx={{ width: 100 }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.damagedQty}
                        onChange={(e) => {
                          const n = [...unloadItems];
                          n[idx].damagedQty = e.target.value;
                          setUnloadItems(n);
                        }}
                        sx={{ width: 100 }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUnloadDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUnload}>
            Confirm Unload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VanTripDetail;
